import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Typography,
  Tag,
  Tabs,
  Table,
  Spin,
  InputNumber,
  DatePicker,
  Checkbox,
  Divider,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic,
  Descriptions,
  Switch,
  Select,
} from 'antd';
import {
  TrophyOutlined,
  UserOutlined,
  CreditCardOutlined,
  SettingOutlined,
  FileSearchOutlined,
} from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import './UserDetail.css';
import { getUserRole } from '../../auth';
import { api, getUserAnalytics } from '../../api';
import dayjs from 'dayjs';
import moment from 'moment-timezone';

const { Text, Title } = Typography;
const { TextArea } = Input;

// ランク表示名マッピング
const rankDisplayNames = {
  white_1: 'ホワイト I',
  white_2: 'ホワイト II',
  white_3: 'ホワイト III',
  white_4: 'ホワイト IV',
  white_5: 'ホワイト V',
  bronze_1: 'ブロンズ I',
  bronze_2: 'ブロンズ II',
  bronze_3: 'ブロンズ III',
  silver_1: 'シルバー I',
  silver_2: 'シルバー II',
  silver_3: 'シルバー III',
  gold_1: 'ゴールド I',
  gold_2: 'ゴールド II',
  gold_3: 'ゴールド III',
  platinum_1: 'プラチナ I',
  platinum_2: 'プラチナ II',
  platinum_3: 'プラチナ III',
  diamond_1: 'ダイヤモンド I',
  diamond_2: 'ダイヤモンド II',
  diamond_3: 'ダイヤモンド III',
  legend: 'レジェンド',
};

const rankColors = {
  white_1: '#8c8c8c',
  white_2: '#8c8c8c',
  white_3: '#8c8c8c',
  white_4: '#8c8c8c',
  white_5: '#8c8c8c',
  bronze_1: '#cd7f32',
  bronze_2: '#cd7f32',
  bronze_3: '#cd7f32',
  silver_1: '#a0a0a0',
  silver_2: '#a0a0a0',
  silver_3: '#a0a0a0',
  gold_1: '#faad14',
  gold_2: '#faad14',
  gold_3: '#faad14',
  platinum_1: '#722ed1',
  platinum_2: '#722ed1',
  platinum_3: '#722ed1',
  diamond_1: '#1890ff',
  diamond_2: '#1890ff',
  diamond_3: '#1890ff',
  legend: '#eb2f96',
};

// 機能オプション（解放済み機能の選択肢）
const featureOptions = [
  { value: 'research_share', label: 'リサーチ共有（パスワード保護）' },
  { value: 'custom_style', label: 'カスタムスタイル登録' },
  { value: 'referral_system', label: '紹介機能' },
  { value: 'topping_data', label: '独自情報登録' },
  { value: 'style_fine_tuning', label: 'スタイル微調整' },
  { value: 'fact_check', label: 'ファクトチェックモード' },
];

const UserDetail = ({ visible, onCancel, userId, onUpdate }) => {
  const [form] = Form.useForm();
  const [customPlanForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [creditLogs, setCreditLogs] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({
    totalArticles: 0,
    totalPayment: 0,
    subscriptionMonths: 0,
    loginCount60Days: 0,
    totalLoginDays: 0,
    engagementData: [],
  });
  const [gamificationData, setGamificationData] = useState(null);
  const [usageHistory, setUsageHistory] = useState({
    analyses: [],
    generations: [],
    factChecks: [],
    analysesPagination: { current: 1, pageSize: 5, total: 0 },
    generationsPagination: { current: 1, pageSize: 5, total: 0 },
  });
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = await getUserRole();
      setCurrentUserRole(role);
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    if (visible && userId) {
      setActiveTab('1');
      fetchUserDetails();
      fetchTransactions();
      fetchCreditLogs();
      fetchAnalyticsData();
      fetchGamificationData();
      fetchUsageHistory(1, 1);
    }
  }, [visible, userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const timestamp = new Date().getTime();
      const response = await api.get(`/users/${userId}?_=${timestamp}`);
      setUserData(response.data);

      const earlyAccess = response.data.early_access || {};

      // 古い機能キーを新しいキーに正規化
      const normalizeFeatureKey = (key) => {
        const keyMapping = {
          'topping': 'topping_data',
          'style_adjustment': 'style_fine_tuning',
        };
        return keyMapping[key] || key;
      };
      const rawFeatures = response.data.unlocked_features || [];
      const normalizedFeatures = rawFeatures.map(normalizeFeatureKey);

      form.setFieldsValue({
        ...response.data,
        unlocked_features: normalizedFeatures,
        plan: response.data.plan?.name,
        next_renewal_date: response.data.subscription?.next_renewal_date
          ? dayjs(response.data.subscription.next_renewal_date)
          : null,
        stripe_customer_id: response.data.stripe_customer_id,
        bonus_credit: response.data.bonus_credit || 0,
        early_access: {
          fact_check: earlyAccess.fact_check === true,
          keyword_recommend: earlyAccess.keyword_recommend === true,
        },
      });
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      message.error('ユーザー詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const response = await getUserAnalytics(userId);
      setAnalyticsData(response);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    }
  };

  const fetchGamificationData = async () => {
    try {
      const response = await api.get(`/api/gamification/admin/user/${userId}`);
      setGamificationData(response.data);
      // unlocked_featuresをフォームに設定（古いキーを正規化）
      const normalizeFeatureKey = (key) => {
        const keyMapping = {
          'topping': 'topping_data',
          'style_adjustment': 'style_fine_tuning',
        };
        return keyMapping[key] || key;
      };
      const rawFeatures = response.data?.unlocked_features || [];
      const normalizedFeatures = rawFeatures.map(normalizeFeatureKey);
      form.setFieldsValue({
        unlocked_features: normalizedFeatures,
      });
    } catch (error) {
      console.error('Failed to fetch gamification data:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await api.get(`/subscriptions/transactions/${userId}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchCreditLogs = async () => {
    try {
      const response = await api.get(`/subscriptions/ticket-logs/${userId}`);
      setCreditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch credit logs:', error);
    }
  };

  const fetchUsageHistory = async (analysesPage = 1, generationsPage = 1) => {
    try {
      const pageSize = 5;

      // AIO分析
      const analysesRes = await api.get('/api/v2/analysis/list', {
        params: {
          user_id: userId,
          skip: (analysesPage - 1) * pageSize,
          limit: pageSize,
        },
      });

      // AIO生成
      const generationsRes = await api.get('/api/v2/articles', {
        params: {
          user_id: userId,
          skip: (generationsPage - 1) * pageSize,
          limit: pageSize,
        },
      });

      // ファクトチェック
      let factChecks = [];
      try {
        const factCheckRes = await api.get(`/fact-check/history/${userId}`);
        factChecks = factCheckRes.data || [];
      } catch (e) {
        // ファクトチェック履歴がない場合は無視
      }

      setUsageHistory(prev => ({
        analyses: analysesRes.data?.analyses || [],
        generations: generationsRes.data?.articles || [],
        factChecks,
        analysesPagination: {
          current: analysesPage,
          pageSize,
          total: analysesRes.data?.total_count || 0,
        },
        generationsPagination: {
          current: generationsPage,
          pageSize,
          total: generationsRes.data?.total_count || 0,
        },
      }));
    } catch (error) {
      console.error('Failed to fetch usage history:', error);
    }
  };

  const onFinish = async values => {
    setLoading(true);
    try {
      const earlyAccess = {
        fact_check: Boolean(values.early_access?.fact_check),
        keyword_recommend: Boolean(values.early_access?.keyword_recommend),
      };

      const updatedValues = {
        company_name: values.company_name,
        department_name: values.department_name,
        email: values.email,
        last_name: values.last_name,
        first_name: values.first_name,
        last_name_kana: values.last_name_kana,
        first_name_kana: values.first_name_kana,
        note: values.note,
        early_access: earlyAccess,
      };

      // masterのみStripe IDとボーナスクレジットを更新可能
      if (currentUserRole === 'master') {
        updatedValues.stripe_customer_id = values.stripe_customer_id;
        if (values.bonus_credit !== undefined) {
          updatedValues.bonus_credit = values.bonus_credit;
        }
        if (values.next_renewal_date) {
          updatedValues.next_renewal_date = dayjs(
            values.next_renewal_date,
          ).format('YYYY-MM-DD');
        }
      }

      await api.put(`/users/${userId}`, updatedValues);

      // 解放済み機能の更新（管理者のみ）
      if (
        (currentUserRole === 'master' || currentUserRole === 'admin') &&
        values.unlocked_features !== undefined
      ) {
        await api.put(
          `/api/gamification/admin/user/${userId}/unlocked-features`,
          {
            features: values.unlocked_features || [],
          },
        );
      }

      message.success('ユーザー情報を更新しました');

      const timestamp = new Date().getTime();
      const updatedUserResponse = await api.get(
        `/users/${userId}?_=${timestamp}`,
      );
      setUserData(updatedUserResponse.data);

      form.setFieldsValue({
        ...updatedUserResponse.data,
        plan: updatedUserResponse.data.plan?.name,
        next_renewal_date: updatedUserResponse.data.subscription
          ?.next_renewal_date
          ? dayjs(updatedUserResponse.data.subscription.next_renewal_date)
          : null,
        stripe_customer_id: updatedUserResponse.data.stripe_customer_id,
        bonus_credit: updatedUserResponse.data.bonus_credit || 0,
        early_access: updatedUserResponse.data.early_access || {},
      });

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update user details:', error);
      message.error(
        `ユーザー情報の更新に失敗しました: ${error.response?.data?.detail || '不明なエラー'}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const isEditable = field => {
    if (currentUserRole === 'master') {
      return [
        'company_name',
        'department_name',
        'email',
        'last_name',
        'first_name',
        'last_name_kana',
        'first_name_kana',
        'plan',
        'next_renewal_date',
        'stripe_customer_id',
        'bonus_credit',
        'note',
        'early_access',
      ].includes(field);
    }
    if (
      currentUserRole === 'admin' &&
      (field === 'note' || field === 'early_access')
    )
      return true;
    return false;
  };

  const roleTags = {
    user: { color: 'blue', text: 'User' },
    parent: { color: 'green', text: 'Parent' },
    admin: { color: 'volcano', text: 'Admin' },
    master: { color: 'red', text: 'Master' },
  };

  const planTags = {
    free: { color: 'default', text: 'フリープラン' },
    lite: { color: 'blue', text: 'ライトプラン' },
    standard: { color: 'success', text: 'スタンダードプラン' },
    standard_new: { color: 'success', text: 'スタンダードプラン' },
    pro: { color: 'blue', text: 'プロプラン' },
    premium: { color: 'warning', text: 'プレミアムプラン' },
    premium_new: { color: 'warning', text: 'プレミアムプラン' },
    custom: { color: 'purple', text: 'カスタムプラン' },
  };

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text).then(
      () => message.success('Stripeの支払いIDをコピーしました'),
      () => message.error('コピーに失敗しました'),
    );
  };

  // 次回クレジットリセット日を計算
  const getNextCreditResetDate = () => {
    if (!userData?.subscription) return '-';
    const nextTicketUpdate = userData.subscription.next_ticket_update;
    if (!nextTicketUpdate) return '-';
    return dayjs(nextTicketUpdate).format('YYYY/MM/DD');
  };

  // 基本情報タブ
  const renderBasicInfoTab = () => {
    const currentRank = gamificationData?.current_rank;
    const displayRank = currentRank
      ? rankDisplayNames[currentRank] || currentRank
      : '-';
    const rankColor = currentRank
      ? rankColors[currentRank] || '#8c8c8c'
      : '#8c8c8c';

    return (
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
              <UserOutlined /> 基本情報
            </Title>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="会社名">
                <Form.Item name="company_name" style={{ margin: 0 }}>
                  <Input disabled={!isEditable('company_name')} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="部署名">
                <Form.Item name="department_name" style={{ margin: 0 }}>
                  <Input disabled={!isEditable('department_name')} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="氏名">
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name="last_name" style={{ margin: 0 }}>
                      <Input
                        disabled={!isEditable('last_name')}
                        placeholder="姓"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="first_name" style={{ margin: 0 }}>
                      <Input
                        disabled={!isEditable('first_name')}
                        placeholder="名"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Descriptions.Item>
              <Descriptions.Item label="氏名（カナ）">
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name="last_name_kana" style={{ margin: 0 }}>
                      <Input
                        disabled={!isEditable('last_name_kana')}
                        placeholder="セイ"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="first_name_kana" style={{ margin: 0 }}>
                      <Input
                        disabled={!isEditable('first_name_kana')}
                        placeholder="メイ"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Descriptions.Item>
              <Descriptions.Item label="メールアドレス">
                <Form.Item name="email" style={{ margin: 0 }}>
                  <Input disabled={!isEditable('email')} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="登録日">
                {userData?.registered_at
                  ? dayjs(userData.registered_at).format('YYYY/MM/DD HH:mm')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="最終ログイン">
                {analyticsData.lastLoginAt
                  ? dayjs(analyticsData.lastLoginAt).format('YYYY/MM/DD HH:mm')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          <Col xs={24} md={12}>
            <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
              <CreditCardOutlined /> プラン・クレジット
            </Title>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="プラン">
                <Tag color={planTags[userData?.plan?.name || 'free']?.color}>
                  {userData?.plan?.display_name || 'フリープラン'}
                </Tag>
                {userData?.subscription?.status === 'trialing' && (
                  <Tag color="purple" style={{ marginLeft: 8 }}>
                    トライアル中
                  </Tag>
                )}
                {userData?.subscription?.status === 'canceling' && (
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    キャンセル予約中
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="クレジット使用量">
                <Text strong>{userData?.credit_used || 0}</Text>
                <Text type="secondary">
                  {' '}
                  / {userData?.subscription?.custom_data?.credit_limit != null
                    ? userData.subscription.custom_data.credit_limit
                    : (userData?.plan?.monthly_tickets || 0)}
                  （{userData?.plan?.type === 'custom' ? '期間内上限' : '月間上限'}）
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="ボーナスクレジット">
                {currentUserRole === 'master' ? (
                  <Form.Item name="bonus_credit" style={{ margin: 0 }}>
                    <InputNumber min={0} style={{ width: 100 }} />
                  </Form.Item>
                ) : (
                  <Text style={{ color: '#722ed1' }}>
                    {userData?.bonus_credit || 0}
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="次回クレジットリセット">
                {getNextCreditResetDate()}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe ID">
                {currentUserRole === 'master' ? (
                  <Form.Item name="stripe_customer_id" style={{ margin: 0 }}>
                    <Input />
                  </Form.Item>
                ) : (
                  <Text copyable>{userData?.stripe_customer_id || '-'}</Text>
                )}
              </Descriptions.Item>
            </Descriptions>

            <Title
              level={5}
              style={{ marginTop: 24, marginBottom: 16, color: '#1890ff' }}
            >
              <TrophyOutlined /> ゲーミフィケーション
            </Title>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="ランク">
                <Tag
                  icon={<TrophyOutlined />}
                  style={{ borderColor: rankColor, color: rankColor }}
                >
                  {displayRank}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="総EXP">
                {gamificationData?.total_exp || 0} EXP
              </Descriptions.Item>
              <Descriptions.Item label="ログイン連続日数">
                {gamificationData?.consecutive_login_days || 0} 日
              </Descriptions.Item>
              <Descriptions.Item label="紹介成功数">
                {gamificationData?.referral_count || 0} 人
              </Descriptions.Item>
              <Descriptions.Item label="チュートリアル">
                {gamificationData?.tutorial_completed ? (
                  <Tag color="success">完了</Tag>
                ) : (
                  <Tag color="warning">未完了</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        <Divider />

        <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
          <SettingOutlined /> 管理者設定
        </Title>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="早期アクセス機能">
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <Form.Item
                    name={['early_access', 'fact_check']}
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Checkbox disabled={!isEditable('early_access')}>
                      ファクトチェックモード
                    </Checkbox>
                  </Form.Item>
                  <Form.Item
                    name={['early_access', 'keyword_recommend']}
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Checkbox disabled={!isEditable('early_access')}>
                      おすすめキーワード
                    </Checkbox>
                  </Form.Item>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="解放済み機能">
                <Form.Item name="unlocked_features" noStyle>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="機能を選択"
                    options={featureOptions}
                    allowClear
                  />
                </Form.Item>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="note" label="備考">
              <TextArea rows={3} disabled={!isEditable('note')} />
            </Form.Item>
          </Col>
        </Row>

        {(currentUserRole === 'master' || currentUserRole === 'admin') && (
          <Form.Item style={{ marginTop: 16, textAlign: 'right' }}>
            <Button type="primary" htmlType="submit">
              更新
            </Button>
          </Form.Item>
        )}
      </Form>
    );
  };

  // 分析タブ
  const renderAnalyticsTab = () => {
    const chartData = {
      labels: analyticsData.engagementData.map(d =>
        dayjs(d.date).format('MM/DD'),
      ),
      datasets: [
        {
          label: 'エンゲージメントスコア',
          data: analyticsData.engagementData.map(d => d.engagementScore),
          fill: false,
          borderColor: 'rgb(0, 212, 255)',
          tension: 0.1,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      plugins: { legend: { position: 'top' }, title: { display: false } },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: 'スコア' } },
        x: { title: { display: true, text: '日付' } },
      },
    };

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="AIO生成数"
                value={analyticsData.totalAioGenerations || 0}
                suffix="本"
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="AIOリサーチ数"
                value={analyticsData.totalAioAnalyses || 0}
                suffix="件"
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="総支払額"
                value={analyticsData.totalPayment}
                prefix="¥"
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="継続月数"
                value={analyticsData.subscriptionMonths || '-'}
                suffix={analyticsData.subscriptionMonths ? 'ヶ月' : ''}
              />
            </Card>
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="総ログイン日数"
                value={analyticsData.totalLoginDays}
                suffix="日"
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="総生成数（旧SEO含む）"
                value={analyticsData.totalArticles}
                suffix="本"
                valueStyle={{ fontSize: 18, color: '#8c8c8c' }}
              />
            </Card>
          </Col>
        </Row>

        <Title
          level={5}
          style={{ marginTop: 24, marginBottom: 16, color: '#1890ff' }}
        >
          流入経路情報
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="ソース"
                value={userData?.utm_source || '-'}
                valueStyle={{ fontSize: 14 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="メディア"
                value={userData?.utm_medium || '-'}
                valueStyle={{ fontSize: 14 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="キャンペーン"
                value={userData?.utm_campaign || '-'}
                valueStyle={{ fontSize: 14 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="参照元"
                value={userData?.ref || '-'}
                valueStyle={{ fontSize: 14 }}
              />
            </Card>
          </Col>
        </Row>

        <Title
          level={5}
          style={{ marginTop: 24, marginBottom: 16, color: '#1890ff' }}
        >
          エンゲージメントスコア（直近30日間）
        </Title>
        {analyticsData.engagementData.length > 0 ? (
          <Line data={chartData} options={chartOptions} />
        ) : (
          <Text type="secondary">データがありません</Text>
        )}
      </div>
    );
  };

  // 利用履歴タブ
  const renderUsageHistoryTab = () => {
    const analysisColumns = [
      {
        title: 'キーワード',
        dataIndex: 'keyword',
        key: 'keyword',
        sorter: (a, b) => (a.keyword || '').localeCompare(b.keyword || ''),
        render: (text, record) => (
          <a
            onClick={e => {
              e.stopPropagation();
              if (record.status === 'completed') {
                window.open(`/aio/analysis/${record.analysis_id}`, '_blank');
              }
            }}
            style={{
              cursor: record.status === 'completed' ? 'pointer' : 'default',
              color: record.status === 'completed' ? '#1890ff' : 'inherit',
            }}
          >
            {text}
          </a>
        ),
      },
      {
        title: 'ステータス',
        dataIndex: 'status',
        key: 'status',
        render: status => {
          const statusMap = {
            completed: { color: 'success', text: '完了' },
            processing: { color: 'processing', text: '処理中' },
            failed: { color: 'error', text: '失敗' },
          };
          const s = statusMap[status] || { color: 'default', text: status };
          return <Tag color={s.color}>{s.text}</Tag>;
        },
      },
      {
        title: '作成日時',
        dataIndex: 'created_at',
        key: 'created_at',
        render: date => dayjs(date).format('YYYY/MM/DD HH:mm'),
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      },
    ];

    // ステータス正規化（生成結果一覧と同じロジック）
    const normalizeStatus = rec => {
      const s = String(rec.status || '').toLowerCase();
      const step = String(rec.current_step || '').toLowerCase();

      if (s === 'completed') return 'completed';
      if (s === 'step4_completed') return 'step4_completed';

      if (step === '4.1' || step === 'step4.1') return 'step4_1';
      if (step === '4.2' || step === 'step4.2') return 'step4_2';
      if (step === '4.3' || step === 'step4.3') return 'step4_3';
      if (step === '4.4' || step === 'step4.4') return 'step4_4';

      if (s === 'draft' || s === 'step2' || s === 'step2_completed')
        return 'step2';
      if (s === 'step3' || s === 'step3_completed') return 'step3';
      if (s.startsWith('step4') || s === 'generating') return 'step4';
      if (s === 'error' || s === 'failed') return 'error';

      return s || 'queued';
    };

    const generationStatusMap = {
      queued: { text: '準備中', color: 'default' },
      step2: { text: 'ベース選択中', color: 'processing' },
      step3: { text: '構成編集中', color: 'geekblue' },
      step4_1: { text: '執筆計画中', color: 'cyan' },
      step4_2: { text: '本文生成中', color: 'blue' },
      step4_3: { text: '推敲中', color: 'purple' },
      step4_4: { text: '最終調整中', color: 'orange' },
      step4: { text: '執筆計画中', color: 'cyan' },
      step4_completed: { text: '生成完了', color: 'success' },
      completed: { text: '生成完了', color: 'success' },
      error: { text: 'エラー', color: 'error' },
    };

    const generationColumns = [
      {
        title: 'キーワード',
        dataIndex: 'keyword',
        key: 'keyword',
        sorter: (a, b) => (a.keyword || '').localeCompare(b.keyword || ''),
        render: (text, record) => (
          <a
            onClick={e => {
              e.stopPropagation();
              // 生成結果ページへ遷移（uuidを使用）
              if (record.uuid) {
                window.open(`/aio/generation/${record.uuid}`, '_blank');
              }
            }}
            style={{
              cursor: record.uuid ? 'pointer' : 'default',
              color: record.uuid ? '#1890ff' : 'inherit',
            }}
          >
            {text}
          </a>
        ),
      },
      {
        title: 'ステータス',
        dataIndex: 'status',
        key: 'status',
        render: (_, rec) => {
          const norm = normalizeStatus(rec);
          const meta = generationStatusMap[norm] || {
            text: rec.status,
            color: 'default',
          };
          return <Tag color={meta.color}>{meta.text}</Tag>;
        },
      },
      {
        title: '作成日時',
        dataIndex: 'created_at',
        key: 'created_at',
        render: date => dayjs(date).format('YYYY/MM/DD HH:mm'),
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      },
    ];

    const factCheckColumns = [
      {
        title: 'URL',
        dataIndex: 'url',
        key: 'url',
        ellipsis: true,
      },
      {
        title: 'ステータス',
        dataIndex: 'status',
        key: 'status',
        render: status => {
          const statusMap = {
            completed: { color: 'success', text: '完了' },
            processing: { color: 'processing', text: '処理中' },
            failed: { color: 'error', text: '失敗' },
          };
          const s = statusMap[status] || { color: 'default', text: status };
          return <Tag color={s.color}>{s.text}</Tag>;
        },
      },
      {
        title: '実行日時',
        dataIndex: 'created_at',
        key: 'created_at',
        render: date => dayjs(date).format('YYYY/MM/DD HH:mm'),
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      },
    ];

    return (
      <div>
        <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
          <FileSearchOutlined /> AIO分析履歴
        </Title>
        <Table
          columns={analysisColumns}
          dataSource={usageHistory.analyses}
          rowKey="analysis_id"
          pagination={{
            ...usageHistory.analysesPagination,
            onChange: page =>
              fetchUsageHistory(
                page,
                usageHistory.generationsPagination.current,
              ),
            showSizeChanger: false,
          }}
          size="small"
        />

        <Title
          level={5}
          style={{ marginTop: 24, marginBottom: 16, color: '#1890ff' }}
        >
          <FileSearchOutlined /> AIO生成履歴
        </Title>
        <Table
          columns={generationColumns}
          dataSource={usageHistory.generations}
          rowKey="id"
          pagination={{
            ...usageHistory.generationsPagination,
            onChange: page =>
              fetchUsageHistory(usageHistory.analysesPagination.current, page),
            showSizeChanger: false,
          }}
          size="small"
        />

        {usageHistory.factChecks.length > 0 && (
          <>
            <Title
              level={5}
              style={{ marginTop: 24, marginBottom: 16, color: '#1890ff' }}
            >
              <FileSearchOutlined /> ファクトチェック履歴
            </Title>
            <Table
              columns={factCheckColumns}
              dataSource={usageHistory.factChecks}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </>
        )}
      </div>
    );
  };

  // 決済履歴タブ
  const renderTransactionsTab = () => {
    const columns = [
      {
        title: '日付',
        dataIndex: 'created_at',
        key: 'created_at',
        render: date => dayjs(date).format('YYYY/MM/DD HH:mm'),
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        defaultSortOrder: 'descend',
      },
      {
        title: '金額（税込）',
        dataIndex: 'amount',
        key: 'amount',
        render: amount => `¥${amount.toLocaleString()}`,
        sorter: (a, b) => a.amount - b.amount,
      },
      {
        title: 'ステータス',
        dataIndex: 'status',
        key: 'status',
        render: status => (
          <Tag color={status === 'succeeded' ? 'green' : 'red'}>
            {status === 'succeeded' ? '成功' : '失敗'}
          </Tag>
        ),
      },
      {
        title: '内容',
        dataIndex: 'plan_name',
        key: 'plan_name',
        render: (planName, record) => {
          const planType = planTags[planName]?.text || planName;
          const paymentType = record.is_yearly ? '年払い' : '月払い';
          const transactionType = record.is_new ? '新規' : '継続';
          return planType === 'フリープラン'
            ? `${planType}（${transactionType}）`
            : `${planType}（${transactionType}／${paymentType}）`;
        },
      },
    ];

    return (
      <div>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          onRow={record => ({
            onClick: () => copyToClipboard(record.stripe_payment_intent_id),
            style: { cursor: 'pointer' },
          })}
          size="small"
        />
        <Text type="secondary">
          行をクリックするとStripe支払いIDをコピーできます
        </Text>
      </div>
    );
  };

  // クレジット履歴タブ
  const renderCreditLogsTab = () => {
    const columns = [
      {
        title: '日時',
        dataIndex: 'created_at',
        key: 'created_at',
        render: date => dayjs(date).format('YYYY/MM/DD HH:mm'),
        sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        defaultSortOrder: 'descend',
      },
      {
        title: '変更前',
        dataIndex: 'previous_count',
        key: 'previous_count',
        align: 'right',
        sorter: (a, b) => a.previous_count - b.previous_count,
      },
      {
        title: '変更後',
        dataIndex: 'new_count',
        key: 'new_count',
        align: 'right',
        sorter: (a, b) => a.new_count - b.new_count,
      },
      { title: '理由', dataIndex: 'reason', key: 'reason' },
    ];

    return (
      <div>
        <Table
          columns={columns}
          dataSource={creditLogs}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </div>
    );
  };

  // カスタムプランタブ
  const renderCustomPlanTab = () => {
    const subscription = userData?.subscription;

    const handleCustomPlanSubmit = async values => {
      setLoading(true);
      try {
        const formData = {
          end_date: dayjs(values.end_date).format('YYYY-MM-DD'),
          target_user_id: userId,
          total_amount: values.total_amount,
          credit_limit: values.credit_limit,
          bonus_credit: values.bonus_credit || 0,
        };
        await api.post('/subscriptions/create-custom-plan', formData);
        message.success('カスタムプランが作成されました。承認待ちです。');
        customPlanForm.resetFields();
        fetchUserDetails();
        if (onUpdate) onUpdate();
      } catch (error) {
        message.error(
          'カスタムプランの作成に失敗しました: ' +
            (error.response?.data?.detail || error.message),
        );
      } finally {
        setLoading(false);
      }
    };

    const handleApproveCustomPlan = async () => {
      setLoading(true);
      try {
        await api.post(`/subscriptions/approve-custom-plan/${subscription.id}`);
        message.success('カスタムプランが承認されました');
        fetchUserDetails();
        if (onUpdate) onUpdate();
      } catch (error) {
        message.error(
          'カスタムプランの承認に失敗しました: ' + error.response?.data?.detail,
        );
      } finally {
        setLoading(false);
      }
    };

    const handleCancelPendingCustomPlan = async () => {
      setLoading(true);
      try {
        await api.post(
          `/subscriptions/cancel-pending-custom-plan/${subscription.id}`,
        );
        message.success('承認待ちのカスタムプランがキャンセルされました');
        fetchUserDetails();
        if (onUpdate) onUpdate();
      } catch (error) {
        message.error(
          'キャンセルに失敗しました: ' + error.response?.data?.detail,
        );
      } finally {
        setLoading(false);
      }
    };

    const handleCancelCustomPlan = async () => {
      setLoading(true);
      try {
        await api.post(`/subscriptions/cancel-custom-plan/${subscription.id}`);
        message.success('カスタムプランがキャンセルされました');
        fetchUserDetails();
        if (onUpdate) onUpdate();
      } catch (error) {
        message.error(
          'キャンセルに失敗しました: ' + error.response?.data?.detail,
        );
      } finally {
        setLoading(false);
      }
    };

    const isPending =
      subscription?.status === 'pending' ||
      subscription?.has_pending_custom_plan;
    const isActiveCustom =
      subscription?.status === 'active' && subscription?.plan_id === 6;
    const pendingPlan =
      subscription?.status === 'pending'
        ? subscription
        : subscription?.pending_custom_plan;

    if (isPending && pendingPlan) {
      return (
        <div>
          <Title level={5} style={{ marginBottom: 16, color: '#faad14' }}>
            承認待ちのカスタムプラン
          </Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="期間">
              {moment(pendingPlan.current_period_start).tz('Asia/Tokyo').format('YYYY/MM/DD')} -{' '}
              {moment(pendingPlan.current_period_end).tz('Asia/Tokyo').format('YYYY/MM/DD')}
            </Descriptions.Item>
            <Descriptions.Item label="総額">
              ¥
              {pendingPlan.custom_data?.total_amount?.toLocaleString() || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="期間内クレジット上限">
              {pendingPlan.custom_data?.credit_limit || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="ボーナスクレジット">
              {pendingPlan.custom_data?.bonus_credit || 0}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button
              onClick={handleCancelPendingCustomPlan}
              style={{ marginRight: 8 }}
            >
              申請をキャンセル
            </Button>
            {currentUserRole === 'master' && (
              <Button type="primary" onClick={handleApproveCustomPlan}>
                承認して有効化する
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (isActiveCustom) {
      return (
        <div>
          <Title level={5} style={{ marginBottom: 16, color: '#52c41a' }}>
            アクティブなカスタムプラン
          </Title>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="期間">
              {moment(subscription.current_period_start).tz('Asia/Tokyo').format('YYYY/MM/DD')} -{' '}
              {moment(subscription.current_period_end).tz('Asia/Tokyo').format('YYYY/MM/DD')}
            </Descriptions.Item>
            <Descriptions.Item label="総額">
              ¥
              {subscription.custom_data?.total_amount?.toLocaleString() ||
                'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="期間内クレジット上限">
              {subscription.custom_data?.credit_limit || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="ボーナスクレジット">
              {subscription.custom_data?.bonus_credit || 0}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Popconfirm
              title="カスタムプランをキャンセルしてフリープランに移行しますか？"
              onConfirm={handleCancelCustomPlan}
              okText="はい"
              cancelText="いいえ"
            >
              <Button danger>即時キャンセルする</Button>
            </Popconfirm>
          </div>
        </div>
      );
    }

    // 新規カスタムプラン申請フォーム
    return (
      <div>
        <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
          カスタムプランの登録申請
        </Title>
        <Form
          form={customPlanForm}
          onFinish={handleCustomPlanSubmit}
          layout="vertical"
        >
          <Form.Item
            name="end_date"
            label="終了日"
            rules={[{ required: true, message: '終了日を選択してください' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={current =>
                current && current < dayjs().endOf('day')
              }
              presets={[
                { label: '30日後', value: dayjs().add(30, 'd') },
                { label: '90日後', value: dayjs().add(90, 'd') },
                { label: '180日後', value: dayjs().add(180, 'd') },
                { label: '1年後', value: dayjs().add(1, 'y') },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="total_amount"
            label="総額（税込）"
            rules={[{ required: true, message: '総額を入力してください' }]}
          >
            <InputNumber min={0} addonBefore="¥" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="credit_limit"
            label="期間内クレジット上限"
            rules={[
              { required: true, message: 'クレジット上限を入力してください' },
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="bonus_credit" label="ボーナスクレジット（任意）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit">
              カスタムプランの登録を申請する
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };

  if (!userData) return null;

  const currentRank = gamificationData?.current_rank;
  const displayRank = currentRank
    ? rankDisplayNames[currentRank] || currentRank
    : null;
  const rankColor = currentRank
    ? rankColors[currentRank] || '#8c8c8c'
    : '#8c8c8c';

  const tabItems = [
    { key: '1', label: '基本情報', children: renderBasicInfoTab() },
    { key: '2', label: '分析', children: renderAnalyticsTab() },
    { key: '3', label: '利用履歴', children: renderUsageHistoryTab() },
    { key: '4', label: '決済履歴', children: renderTransactionsTab() },
    { key: '5', label: 'クレジット履歴', children: renderCreditLogsTab() },
    { key: '6', label: 'カスタムプラン', children: renderCustomPlanTab() },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      width={1200}
      className="u_detail_modal"
    >
      <Spin spinning={loading}>
        <Row style={{ marginBottom: 16, alignItems: 'center' }} gutter={16}>
          <Col flex="auto">
            <div>
              <Text type="secondary">
                {userData.company_name} {userData.department_name}
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 18 }}>
                {userData.last_name} {userData.first_name}
              </Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({userData.last_name_kana} {userData.first_name_kana})
              </Text>
            </div>
            <div>
              <Text type="secondary">{userData.email}</Text>
              <Text type="secondary" style={{ marginLeft: 16, fontSize: 11 }}>
                ID: {userData.user_id}
              </Text>
            </div>
          </Col>
          <Col>
            <Tag color={roleTags[userData.role]?.color}>
              {roleTags[userData.role]?.text}
            </Tag>
            <Tag color={planTags[userData.plan?.name || 'free']?.color}>
              {planTags[userData.plan?.name || 'free']?.text}
            </Tag>
            {displayRank && (
              <Tag
                icon={<TrophyOutlined />}
                style={{ borderColor: rankColor, color: rankColor }}
              >
                {displayRank}
              </Tag>
            )}
          </Col>
        </Row>

        <Divider style={{ margin: '16px 0' }} />

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Spin>
    </Modal>
  );
};

export default UserDetail;
